import { useInfiniteQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import {
  type StyleCatalogResponse,
  type StyleOption
} from '../../../shared/api/contracts/styleTransferSchemas';
import { adaptStyleCatalog } from '../../../shared/api/intelliSightAdapters';
import { requestWithAuthRetry } from '../../auth/api/authenticatedRequest';
import { useSessionStore } from '../state/sessionStore';
import {
  buildStylesCacheKey,
  getCachedStyles,
  isCacheEntryExpired,
  pruneExpiredStylesCache,
  saveStylesCachePages
} from '../storage/stylesCacheDatabase';

const DEFAULT_PAGE_SIZE = 18;

export const STYLE_CATALOG_QUERY_KEY = ['style-transfer', 'styles'] as const;

function sortStyles(styles: StyleOption[]): StyleOption[] {
  return [...styles].sort((a, b) => {
    const toTimestamp = (value?: string) => {
      if (!value) {
        return Number.NaN;
      }
      const time = Date.parse(value);
      return Number.isFinite(time) ? time : Number.NaN;
    };

    const aTime = toTimestamp(a.updatedAt);
    const bTime = toTimestamp(b.updatedAt);

    if (!Number.isNaN(aTime) || !Number.isNaN(bTime)) {
      if (Number.isNaN(aTime)) {
        return 1;
      }
      if (Number.isNaN(bTime)) {
        return -1;
      }
      if (aTime !== bTime) {
        return bTime - aTime;
      }
    }

    return a.name.localeCompare(b.name);
  });
}

async function fetchCatalogPage(page: number, size: number): Promise<StyleCatalogResponse> {
  const session = useSessionStore.getState();
  const token = session.token;
  const cacheKey = buildStylesCacheKey({
    tokenType: token?.tokenType ?? null,
    locale: i18next.language,
    pageSize: size
  });

  const cachedEntry = await getCachedStyles(cacheKey);
  const cachedValid = cachedEntry && !isCacheEntryExpired(cachedEntry);
  if (cachedValid) {
    const cachedPage = cachedEntry.pages.find((entry) => entry.pageInfo.page === page);
    if (cachedPage) {
      return {
        ...cachedPage,
        items: sortStyles(cachedPage.items)
      };
    }
  }

  const searchParams = new URLSearchParams({
    page: String(page),
    size: String(size)
  });

  const response = await requestWithAuthRetry('GET', `/api/v1/image-styles?${searchParams.toString()}`, {
    authToken: token,
    retryLabel: `style-catalog-page-${page}`
  });
  const adapted = adaptStyleCatalog(response);
  const normalized = {
    ...adapted,
    items: sortStyles(adapted.items)
  };
  await saveStylesCachePages(cacheKey, [normalized]);
  void pruneExpiredStylesCache();
  return normalized;
}

export function useStyleCatalog(pageSize = DEFAULT_PAGE_SIZE) {
  const locale = i18next.language;
  return useInfiniteQuery({
    queryKey: [...STYLE_CATALOG_QUERY_KEY, { pageSize, locale }],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNext ? lastPage.pageInfo.page + 1 : undefined),
    getPreviousPageParam: (firstPage) =>
      firstPage.pageInfo.hasPrevious && firstPage.pageInfo.page > 0
        ? Math.max(firstPage.pageInfo.page - 1, 0)
        : undefined,
    queryFn: ({ pageParam = 0 }) => fetchCatalogPage(pageParam, pageSize),
    staleTime: 5 * 60_000,
    suspense: false
  });
}
